import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CornerUpLeft, Paperclip, Send, X } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { api, mensagemDeErro, post } from '../../nucleo/api';
import { nomeDeArquivo, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { useNomesDoChat } from './dados';
import { nomeDoCanalNaTela, tamanhoArquivo, type Anexo, type CanalDetalhe, type Mensagem } from './tipos';

/** Tipos que o seletor oferece (o servidor confere pelos bytes de qualquer jeito). */
const ACEITOS = 'image/jpeg,image/png,image/webp,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.txt,.csv,.zip,audio/ogg,audio/mpeg,audio/mp4,video/mp4';

/** "@termo" logo antes do cursor (sem espaço): abre as sugestões de quem participa do canal. */
export function termoDaMencao(textoAteCursor: string): string | null {
  const m = /(^|\s)@([^\s@]{0,30})$/u.exec(textoAteCursor);
  return m ? m[2]!.toLocaleLowerCase() : null;
}

/**
 * Campo de mensagem do chat: Enter envia, Shift+Enter quebra a linha, "@" sugere quem participa do canal (combobox no
 * padrão da busca remota: setas, Enter, Esc e Tab), anexos enviados antes ao canal e a resposta citando outra mensagem.
 */
export function Composer({ canal, resposta, aoCancelarResposta }: { canal: CanalDetalhe; resposta: Mensagem | null; aoCancelarResposta: () => void }) {
  const { t, i18n } = useTranslation('equipe');
  const eu = useSessao().usuario.id;
  const toast = useToast();
  const qc = useQueryClient();
  const [texto, setTexto] = useState('');
  const [cursor, setCursor] = useState(0);
  const [indice, setIndice] = useState(0);
  const [fechada, setFechada] = useState(false);
  const [mencoes, setMencoes] = useState<Record<string, string>>({});
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  // Posição do cursor depois de escolher a menção: aplicada logo após o commit, antes da próxima tecla.
  const [cursorPendente, setCursorPendente] = useState<number | null>(null);
  const idLista = useId();
  const campo = useRef<HTMLTextAreaElement>(null);
  const seletor = useRef<HTMLInputElement>(null);
  const termo = termoDaMencao(texto.slice(0, cursor));
  const sugestoes = useMemo(
    () => (termo === null ? [] : canal.participantes.filter((p) => p.usuario_id !== eu && p.nome.toLocaleLowerCase().includes(termo)).slice(0, 8)),
    [termo, canal.participantes, eu],
  );
  const aberta = sugestoes.length > 0 && !fechada;
  const ativa = Math.min(indice, Math.max(0, sugestoes.length - 1));
  const mencionados = Object.entries(mencoes).filter(([, nome]) => texto.includes(`@${nome}`));
  // W20: nomes que a citação da resposta pode trazer (iniciais no modo discreto com a opção de nomes).
  const nomes = useNomesDoChat([...canal.participantes.map((p) => p.nome), resposta?.autor_nome, ...(resposta?.mencoes ?? []).map((x) => x.nome)]);

  const enviar = useMutation({
    mutationFn: () => post(`/equipe/canais/${canal.id}/mensagens`, { texto, mencoes: mencionados.map(([id]) => id), anexo_ids: anexos.map((a) => a.id), resposta_a: resposta?.id ?? null }),
    onSuccess: async () => {
      setTexto('');
      setMencoes({});
      setAnexos([]);
      aoCancelarResposta();
      await qc.invalidateQueries({ queryKey: ['equipe'] });
      campo.current?.focus();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const subir = useMutation({
    mutationFn: (f: File) => {
      const fd = new FormData();
      fd.append('arquivo', f);
      return api<Anexo>('POST', `/equipe/canais/${canal.id}/anexos`, fd);
    },
    onSuccess: (a) => setAnexos((l) => [...l, a]),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  function escolher(i: number) {
    const p = sugestoes[i];
    if (!p) return;
    const antes = texto.slice(0, cursor).replace(/@([^\s@]{0,30})$/u, `@${p.nome} `);
    const novo = antes + texto.slice(cursor);
    setTexto(novo);
    setMencoes((m) => ({ ...m, [p.usuario_id]: p.nome }));
    setIndice(0);
    setFechada(false);
    setCursor(antes.length);
    setCursorPendente(antes.length);
  }
  useLayoutEffect(() => {
    if (cursorPendente === null) return;
    campo.current?.focus();
    campo.current?.setSelectionRange(cursorPendente, cursorPendente);
    setCursorPendente(null);
  }, [cursorPendente]);
  function disparar() {
    if ((!texto.trim() && !anexos.length) || enviar.isPending || subir.isPending) return;
    enviar.mutate();
  }

  return (
    <div className="relative border-t border-borda p-3" data-composer>
      {aberta && (
        <ul id={idLista} role="listbox" tabIndex={-1} aria-label={t('composer.pessoas')} onMouseDown={(e) => e.preventDefault()} className="absolute right-3 bottom-full left-3 z-20 mb-1 max-h-56 overflow-y-auto rounded-lg border border-borda bg-sup-2 py-1 shadow-xl">
          {sugestoes.map((p, i) => (
            <li
              key={p.usuario_id}
              id={`${idLista}-${i}`}
              role="option"
              aria-selected={i === ativa}
              onClick={() => escolher(i)}
              className={`flex w-full min-w-0 cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm hover:bg-sup-3 ${i === ativa ? 'bg-sup-3 outline-2 -outline-offset-2 outline-foco' : ''}`}
            >
              <span className="text-acento-forte">@</span>
              <TextoCortado className="text-tinta-1">{nomePessoa(p.nome)}</TextoCortado>
            </li>
          ))}
        </ul>
      )}
      {resposta && (
        <div className="mb-2 flex min-w-0 items-start gap-2 rounded-lg border-l-2 border-acento-forte bg-sup-2 px-3 py-2 text-xs">
          <CornerUpLeft className="mt-0.5 size-3.5 shrink-0 text-tinta-3" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-tinta-2">{t('mensagem.respondendo', { nome: nomePessoa(resposta.autor_nome) })}</p>
            <TextoCortado como="p" className="text-tinta-3">
              {resposta.texto ? textoLivre(resposta.texto, ...nomes) : t('mensagem.anexo')}
            </TextoCortado>
          </div>
          <button type="button" aria-label={t('mensagem.cancelarResposta')} onClick={aoCancelarResposta} className="rounded-full p-0.5 text-tinta-3 hover:text-tinta-1">
            <X className="size-3.5" aria-hidden />
          </button>
        </div>
      )}
      {(anexos.length > 0 || subir.isPending) && (
        <ul className="mb-2 flex flex-wrap gap-1.5" aria-live="polite">
          {anexos.map((a) => (
            <li key={a.id} className="flex max-w-full min-w-0 items-center gap-1.5 rounded-full border border-borda bg-sup-2 py-0.5 pr-1 pl-2.5 text-xs text-tinta-2">
              <TextoCortado className="min-w-0">{nomeDeArquivo(a.nome)}</TextoCortado>
              <span className="numero shrink-0 text-tinta-3">{tamanhoArquivo(a.tamanho, i18n.language)}</span>
              <button type="button" aria-label={t('composer.remover', { nome: nomeDeArquivo(a.nome) })} onClick={() => setAnexos((l) => l.filter((x) => x.id !== a.id))} className="rounded-full p-0.5 text-tinta-3 hover:text-tinta-1">
                <X className="size-3.5" aria-hidden />
              </button>
            </li>
          ))}
          {subir.isPending && <li className="text-xs text-tinta-3">{t('composer.enviando')}</li>}
        </ul>
      )}
      <div className="flex items-end gap-2">
        <textarea
          ref={campo}
          rows={2}
          data-campo-mensagem
          aria-label={t('composer.rotulo', { canal: nomeDoCanalNaTela(canal, nomes) })}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={aberta}
          aria-controls={aberta ? idLista : undefined}
          aria-activedescendant={aberta ? `${idLista}-${ativa}` : undefined}
          placeholder={t('composer.placeholder')}
          value={texto}
          maxLength={10000}
          onChange={(e) => {
            setTexto(e.target.value);
            setCursor(e.target.selectionStart ?? e.target.value.length);
            setIndice(0);
            setFechada(false);
          }}
          onSelect={(e) => setCursor(e.currentTarget.selectionStart ?? 0)}
          onBlur={() => setFechada(true)}
          onKeyDown={(e) => {
            if (aberta) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setIndice((ativa + 1) % sugestoes.length);
                return;
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setIndice((ativa - 1 + sugestoes.length) % sugestoes.length);
                return;
              }
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                escolher(ativa);
                return;
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                setFechada(true);
                return;
              }
              if (e.key === 'Tab') {
                setFechada(true);
                return;
              }
            }
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              disparar();
            }
          }}
          className="max-h-40 min-h-12 w-full min-w-0 flex-1 resize-none rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 placeholder:text-tinta-3 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
        />
        <input
          ref={seletor}
          type="file"
          accept={ACEITOS}
          className="hidden"
          tabIndex={-1}
          aria-hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) subir.mutate(f);
          }}
        />
        <Botao aria-label={t('composer.anexar')} title={`${t('composer.anexar')}. ${t('composer.limite')}`} ocupado={subir.isPending} icone={<Paperclip className="size-4" />} onClick={() => seletor.current?.click()} />
        <Botao variante="primario" aria-label={t('composer.enviar')} disabled={!texto.trim() && !anexos.length} carregando={enviar.isPending} icone={<Send className="size-4" />} onClick={disparar}>
          <span className="hidden sm:inline">{t('composer.enviar')}</span>
        </Botao>
      </div>
      <p className="mt-1.5 text-xs text-tinta-3">{mencionados.length ? t('composer.mencionados', { nomes: mencionados.map(([, n]) => nomePessoa(n)).join(', ') }) : t('composer.dica')}</p>
    </div>
  );
}
